import { useStacklineTheme } from '@stackline/ui';
import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import {
  childLifetimeRatingVolumeByStarResponse,
  parseChildLifetimeRatingVolumeByStarPercentResponse,
  parseChildLifetimeRatingVolumeByStarResponse,
  useChildLifetimeRatingVolumeByStarData
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import {
  refineSubtitle,
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useAppSelector } from 'src/utils/Hooks';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const lifetimeRatingRatingVolumeOption = [
  { label: 'Cumulative Rating Volume', id: 'cumulativeRatingVolume' },
  { label: 'Cumulative Rating Volume %', id: 'cumulativeRatingVolumePercent' }
];

/**
 *  Display Top Entity Chart for Reviews V2 Cumulative Rating Volume by star rating.
 * */

const ChildLifetimeRatingVolumeByStarRatingChart = () => {
  const { data, isLoading } = useChildLifetimeRatingVolumeByStarData();
  const productEntity = useProductEntity();
  const fieldLevel = productEntity ? 'parent' : 'child';

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const [selectedMetric, setSelectedMetric] = useState({
    label: 'Cumulative Rating Volume',
    id: 'cumulativeRatingVolume'
  });
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const theme = useStacklineTheme();

  const chartData = data
    ? selectedMetric.id === 'cumulativeRatingVolume'
      ? parseChildLifetimeRatingVolumeByStarResponse({
          data: data.data as childLifetimeRatingVolumeByStarResponse,
          fieldLevel
        })
      : parseChildLifetimeRatingVolumeByStarPercentResponse({
          data: data.data as childLifetimeRatingVolumeByStarResponse,
          fieldLevel
        })
    : [];

  const subtitle = refineSubtitle([entityPhrase, taxonomyText]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => saveSingleBarChartAsDelimitedData(chartData)}
      includeSummaryInfo={false}
      loading={isLoading}
      loadingComponent={<TopEntityChartV2Loading />}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the ${selectedMetric.label.toLocaleLowerCase()} grouped by star rating for `}
            {subtitle}.
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={{
        defaultLabel: '',
        options: lifetimeRatingRatingVolumeOption,
        selectedId: selectedMetric.id,
        onChange: (option) => setSelectedMetric(option),
        suffix: 'by Star Rating'
      }}
      primaryLegendProps={{
        displayName: legendLabel
      }}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            data: chartData,
            metricType: selectedMetric.id === 'cumulativeRatingVolume' ? METRICTYPE.VOLUME : METRICTYPE.PERCENT
          }
        ]}
        seriesColors={[theme.colors.primary]}
      />
    </BeaconChartWithLegend>
  );
};
export default ChildLifetimeRatingVolumeByStarRatingChart;
